import { normalizeSalespersonCode } from './accountCollection';
import { fetchSalespersonEmails } from './applicationFormSettings';
import { searchCffCustomers, searchCffItems } from './customerFeedbackLookup';
import {
  fetchDemCustomer,
  fetchDemCustomerSalespersonCode,
} from './demCustomers';
import { fetchDemItem } from './demItems';
import { fetchPricesForItems, getPriceGroupPrice, type DemPriceRow } from './demPoPricing';

export type MemoAiCustomerLookup = {
  code: string;
  name: string;
  salesperson_code: string;
  salesperson_label: string;
  price_group: string;
};

export type MemoAiSalespersonLookup = {
  code: string;
  display_name: string;
  label: string;
};

export type MemoAiItemLookup = {
  code: string;
  description: string;
  uom: string;
  /** Current packing from DEM item master (use as “old / current” when NOTES change packing). */
  packing: string;
  /** Formatted SGD sales price when found in DEM, else empty. */
  sales_price: string;
  price_note: string;
};

export type MemoAiPortalLookups = {
  customers: MemoAiCustomerLookup[];
  salespersons: MemoAiSalespersonLookup[];
  items: MemoAiItemLookup[];
  unresolved_customer_codes: string[];
  unresolved_sales_codes: string[];
  unresolved_item_codes: string[];
};

const CUSTOMER_CODE_RE = /\b([A-Za-z]\d{3,5}\*?)\b/g;
const SALES_CODE_RE = /\b(SA\d{1,3})\b/gi;
const CUSTOMER_CODE_ONLY = /^[A-Za-z]\d{3,5}\*?$/i;
const SALES_CODE_ONLY = /^SA\d{1,3}$/i;
const LABELED_ITEM_RE =
  /(?:item\s*(?:no\.?|#|code)?|sku|product\s*(?:code|no\.?)?)\s*[:\-–]?\s*([A-Za-z0-9][A-Za-z0-9\-/.]{2,40})/gi;
/** Bare tokens that often look like BTH item nos (letters+digits and/or hyphen). */
const BARE_ITEM_RE = /\b([A-Za-z]{1,8}\d[A-Za-z0-9\-/.]{0,36}|[A-Za-z0-9]{2,}[-/][A-Za-z0-9\-/.]{1,36})\b/g;

const STOP_TOKENS = new Set([
  'THE',
  'AND',
  'FOR',
  'FROM',
  'WITH',
  'THIS',
  'THAT',
  'PLEASE',
  'INFORMED',
  'KINDLY',
  'NOTE',
  'EFFECT',
  'EFFECTIVE',
  'IMMEDIATELY',
  'CUSTOMER',
  'CUSTOMERS',
  'SALES',
  'PRICE',
  'ITEM',
  'ITEMS',
  'MEMO',
  'MEMORANDUM',
]);

function uniquePreserveOrder(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const key = value.trim().toUpperCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(value.trim().toUpperCase());
  }
  return out;
}

function salespersonHead(code: string): string {
  const norm = normalizeSalespersonCode(code);
  const match = norm.match(/^(SA\d{1,3})/i);
  return match ? match[1].toUpperCase() : norm;
}

export function extractCustomerCodesFromNotes(notes: string): string[] {
  const matches = notes.match(CUSTOMER_CODE_RE) ?? [];
  return uniquePreserveOrder(matches);
}

export function extractSalesCodesFromNotes(notes: string): string[] {
  const matches = notes.match(SALES_CODE_RE) ?? [];
  return uniquePreserveOrder(matches);
}

function isLikelyItemCandidate(token: string): boolean {
  const t = token.trim().toUpperCase();
  if (t.length < 3 || t.length > 40) return false;
  if (CUSTOMER_CODE_ONLY.test(t) || SALES_CODE_ONLY.test(t)) return false;
  if (STOP_TOKENS.has(t)) return false;
  if (!/[0-9]/.test(t) && !/[-/]/.test(t)) return false;
  // Pure dates / years
  if (/^\d{1,2}[/-]\d{1,2}([/-]\d{2,4})?$/.test(t)) return false;
  if (/^(19|20)\d{2}$/.test(t)) return false;
  return true;
}

export function extractItemCodeCandidatesFromNotes(notes: string): string[] {
  const labeled: string[] = [];
  LABELED_ITEM_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = LABELED_ITEM_RE.exec(notes)) !== null) {
    if (m[1]) labeled.push(m[1]);
  }

  const bare = notes.match(BARE_ITEM_RE) ?? [];
  const combined = uniquePreserveOrder([...labeled, ...bare].filter(isLikelyItemCandidate));
  // Cap speculative lookups — labeled first (already ordered).
  return combined.slice(0, 15);
}

function salespersonLabel(code: string, displayName: string): string {
  const c = code.trim();
  const name = displayName.trim();
  if (!c) return name;
  if (!name) return c;
  return `${c} | ${name}`;
}

function formatMoneySgd(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '';
  return `$${value.toFixed(2)}`;
}

function pickSalesPrice(
  prices: DemPriceRow[],
  itemCode: string,
  priceGroup: string,
): { sales_price: string; price_note: string } {
  const item = itemCode.trim().toUpperCase();
  const forItem = prices.filter((p) => p.item === item && p.price > 0);
  if (forItem.length === 0) {
    return { sales_price: '', price_note: '' };
  }

  if (priceGroup) {
    const pg = getPriceGroupPrice(forItem, { itemCode: item, priceGroup });
    if (pg > 0) {
      return {
        sales_price: formatMoneySgd(pg),
        price_note: `DEM price group ${priceGroup}`,
      };
    }
  }

  // Prefer All-customers style rows (Nav type 0 / empty / ALL).
  const allRows = forItem.filter(
    (p) =>
      p.type === '0' ||
      p.type === 'all' ||
      p.code === '' ||
      p.code === 'ALL' ||
      p.code === 'ALL CUSTOMERS',
  );
  const pool = allRows.length > 0 ? allRows : forItem;
  const best = Math.min(...pool.map((p) => p.price));
  return {
    sales_price: formatMoneySgd(best),
    price_note: allRows.length > 0 ? 'DEM all-customers sales price' : 'DEM reference sales price',
  };
}

async function resolveCustomer(code: string): Promise<{
  code: string;
  name: string;
  salesperson_code: string;
  price_group: string;
} | null> {
  const want = code.trim().toUpperCase();
  if (!want) return null;

  const candidates = uniquePreserveOrder([
    want,
    want.replace(/\*$/, ''),
    `${want.replace(/\*$/, '')}*`,
  ]);

  for (const candidate of candidates) {
    try {
      const dem = await fetchDemCustomer(candidate);
      if (dem) {
        return {
          code: dem.code.trim().toUpperCase() || want,
          name: dem.name.trim(),
          salesperson_code: dem.salesperson_code.trim(),
          price_group: dem.price_group.trim().toUpperCase(),
        };
      }
    } catch {
      // try next
    }
  }

  try {
    const searchKey = want.replace(/\*$/, '');
    const rows = await searchCffCustomers(searchKey);
    const exact =
      rows.find((row) => row.no.trim().toUpperCase() === want) ??
      rows.find((row) => row.no.trim().toUpperCase().replace(/\*$/, '') === searchKey) ??
      rows.find((row) => row.no.trim().toUpperCase().startsWith(searchKey));
    if (exact) {
      const codeResolved = exact.no.trim().toUpperCase();
      let salesperson = '';
      let price_group = '';
      try {
        salesperson = await fetchDemCustomerSalespersonCode(codeResolved);
      } catch {
        salesperson = '';
      }
      try {
        const dem = await fetchDemCustomer(codeResolved);
        price_group = dem?.price_group.trim().toUpperCase() ?? '';
      } catch {
        price_group = '';
      }
      return {
        code: codeResolved,
        name: exact.name.trim(),
        salesperson_code: salesperson.trim(),
        price_group,
      };
    }
  } catch {
    // unresolved
  }

  return null;
}

async function resolveItem(code: string): Promise<{
  code: string;
  description: string;
  uom: string;
  packing: string;
} | null> {
  const want = code.trim().toUpperCase();
  if (!want) return null;

  try {
    const dem = await fetchDemItem(want);
    if (dem) {
      return {
        code: dem.code.trim().toUpperCase() || want,
        description: dem.description.trim(),
        uom: dem.uom.trim(),
        packing: dem.packing.trim(),
      };
    }
  } catch {
    // fall through
  }

  try {
    const rows = await searchCffItems(want);
    const exact =
      rows.find((row) => row.no.trim().toUpperCase() === want) ??
      rows.find((row) => row.no.trim().toUpperCase().startsWith(want));
    if (exact) {
      // CFF has no packing field — try DEM by resolved code for current packing.
      let packing = '';
      try {
        const dem = await fetchDemItem(exact.no.trim());
        packing = dem?.packing.trim() ?? '';
      } catch {
        packing = '';
      }
      return {
        code: exact.no.trim().toUpperCase(),
        description: exact.description.trim(),
        uom: exact.sales_uom.trim(),
        packing,
      };
    }
  } catch {
    // unresolved
  }

  return null;
}

type SpDirectoryEntry = {
  code: string;
  display_name: string;
  head: string;
};

/**
 * Enrich notes with customer / salesperson / item / sales-price facts from portal DB.
 */
export async function buildMemoAiPortalLookups(notes: string): Promise<MemoAiPortalLookups> {
  const customerCodes = extractCustomerCodesFromNotes(notes);
  const salesCodesFromNotes = extractSalesCodesFromNotes(notes);
  const itemCandidates = extractItemCodeCandidatesFromNotes(notes);

  const salespersonDirectory = await fetchSalespersonEmails().catch(() => []);
  const directory: SpDirectoryEntry[] = [];
  for (const row of salespersonDirectory) {
    if (!row.active) continue;
    const code = String(row.code ?? '').trim();
    if (!code) continue;
    directory.push({
      code,
      display_name: String(row.display_name ?? '').trim(),
      head: salespersonHead(code),
    });
  }

  const findSp = (rawCode: string): SpDirectoryEntry | undefined => {
    const head = salespersonHead(rawCode);
    return (
      directory.find((e) => e.head === head) ??
      directory.find(
        (e) => normalizeSalespersonCode(e.code) === normalizeSalespersonCode(rawCode),
      )
    );
  };

  const customers: MemoAiCustomerLookup[] = [];
  const unresolved_customer_codes: string[] = [];
  const salesFromCustomers = new Set<string>();
  let primaryPriceGroup = '';

  for (const code of customerCodes) {
    const resolved = await resolveCustomer(code);
    if (!resolved) {
      unresolved_customer_codes.push(code);
      continue;
    }
    const spHead = salespersonHead(resolved.salesperson_code);
    if (spHead) salesFromCustomers.add(spHead);
    if (!primaryPriceGroup && resolved.price_group) {
      primaryPriceGroup = resolved.price_group;
    }
    const sp = findSp(resolved.salesperson_code || spHead);
    const labelCode = sp?.code || resolved.salesperson_code || spHead;
    customers.push({
      code: resolved.code,
      name: resolved.name,
      salesperson_code: spHead || resolved.salesperson_code,
      salesperson_label: salespersonLabel(labelCode, sp?.display_name ?? ''),
      price_group: resolved.price_group,
    });
  }

  const allSalesHeads = uniquePreserveOrder([
    ...salesCodesFromNotes,
    ...[...salesFromCustomers],
  ]);

  const salespersons: MemoAiSalespersonLookup[] = [];
  const unresolved_sales_codes: string[] = [];
  for (const head of allSalesHeads) {
    const sp = findSp(head);
    if (sp) {
      salespersons.push({
        code: head,
        display_name: sp.display_name,
        label: salespersonLabel(sp.code, sp.display_name),
      });
      continue;
    }
    if (salesCodesFromNotes.includes(head)) {
      salespersons.push({ code: head, display_name: '', label: head });
      continue;
    }
    unresolved_sales_codes.push(head);
  }

  const items: MemoAiItemLookup[] = [];
  const unresolved_item_codes: string[] = [];
  const resolvedItemCodes: string[] = [];

  for (const code of itemCandidates) {
    // Skip tokens already treated as customers / SA.
    if (CUSTOMER_CODE_ONLY.test(code) || SALES_CODE_ONLY.test(code)) continue;
    if (customers.some((c) => c.code === code || c.code.replace(/\*$/, '') === code.replace(/\*$/, ''))) {
      continue;
    }
    const resolved = await resolveItem(code);
    if (!resolved) {
      // Only report as unresolved when explicitly labeled as item, or exact DEM miss on longer codes.
      if (code.length >= 5) unresolved_item_codes.push(code);
      continue;
    }
    resolvedItemCodes.push(resolved.code);
    items.push({
      code: resolved.code,
      description: resolved.description,
      uom: resolved.uom,
      packing: resolved.packing,
      sales_price: '',
      price_note: '',
    });
  }

  if (resolvedItemCodes.length > 0) {
    try {
      const prices = await fetchPricesForItems(resolvedItemCodes);
      for (const item of items) {
        const priced = pickSalesPrice(prices, item.code, primaryPriceGroup);
        item.sales_price = priced.sales_price;
        item.price_note = priced.price_note;
      }
    } catch {
      // prices optional
    }
  }

  return {
    customers,
    salespersons,
    items,
    unresolved_customer_codes,
    unresolved_sales_codes,
    unresolved_item_codes: uniquePreserveOrder(unresolved_item_codes).filter(
      (c) => !items.some((i) => i.code === c),
    ),
  };
}
